import { QueryKey, useMutation, UseQueryResult } from '@tanstack/react-query'
import { queryClient, QueryOptions, UseMutationResult, useResource, useResources } from '.'
import {
  WorkerCounter,
  FetchWorkerCountersParams,
  fetch,
  get,
  activate,
  deactivate,
  adjustValue,
  AdjustValueParams,
  CreateOrGetWorkerCounterParams,
  createOrGet,
} from '../api/worker-counters'
import { ApiErrorData } from '../api'

/**
 * Query hook to fetch worker counters with filters
 */
export const useWorkerCountersQuery = (
  filters?: FetchWorkerCountersParams,
  options?: QueryOptions<any>,
): UseQueryResult<WorkerCounter[]> =>
  useResources<WorkerCounter[], FetchWorkerCountersParams>(
    'worker-counters',
    fetch,
    filters || {},
    options,
  )

/**
 * Query hook to get a specific worker counter by ID
 */
export const useWorkerCounterQuery = (
  id?: string,
  options?: QueryOptions<any>,
): UseQueryResult<WorkerCounter> => useResource('worker-counters', get, id, undefined, options)

/**
 * Callback function to invalidate worker counters cache on mutation success
 */
const onMutationSuccess = () => {
  queryClient.invalidateQueries(['worker-counters'])
}

/**
 * Mutation hook to activate a worker counter
 */
const useActivateMutationKey = (): QueryKey => ['worker-counters', 'activate']
export const useActivateMutation = (): UseMutationResult<WorkerCounter, string, any> =>
  useMutation(activate, {
    mutationKey: useActivateMutationKey(),
    onSuccess: onMutationSuccess,
  })

/**
 * Mutation hook to deactivate a worker counter
 */
const useDeactivateMutationKey = (): QueryKey => ['worker-counters', 'deactivate']
export const useDeactivateMutation = (): UseMutationResult<WorkerCounter, string, any> =>
  useMutation(deactivate, {
    mutationKey: useDeactivateMutationKey(),
    onSuccess: onMutationSuccess,
  })

/**
 * Mutation hook to adjust the value of a worker counter
 */
const useAdjustValueMutationKey = (): QueryKey => ['worker-counters', 'adjust-value']
export const useAdjustValueMutation = (): UseMutationResult<
  WorkerCounter,
  AdjustValueParams,
  ApiErrorData
> =>
  useMutation(adjustValue, {
    mutationKey: useAdjustValueMutationKey(),
    onSuccess: onMutationSuccess,
  })

/**
 * Mutation hook to create or get a counter for an organization
 */
const useCreateOrGetMutationKey = (): QueryKey => ['worker-counters', 'create-or-get']
export const useCreateOrGetCounterMutation = (): UseMutationResult<
  WorkerCounter,
  CreateOrGetWorkerCounterParams,
  any
> =>
  useMutation(createOrGet, {
    mutationKey: useCreateOrGetMutationKey(),
    onSuccess: onMutationSuccess,
  })
